/**
 * Smart Onboarding Wizard - Umfassende Ersteinrichtung
 * 
 * Sammelt alle wichtigen Informationen um das System perfekt einzurichten:
 * - Betriebsart & Angebot
 * - Tische & Außenbereich
 * - Lieferung & Abholung
 * - Portale (Lieferando, etc.)
 * - Logo & Speisekarte (KI-Import)
 * - Produkt-Kontrolle & Freigabe
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, UtensilsCrossed, Truck, ShoppingBag, Users, TreePine,
  Upload, Sparkles, Check, ArrowRight, ArrowLeft, RefreshCw,
  MapPin, Phone, Mail, Clock, CreditCard, Printer, Globe,
  Pizza, Coffee, Beer, IceCream, Salad, ChefHat, Store,
  CheckCircle, XCircle, Edit, Trash2, Plus, AlertCircle,
  Sun, Moon, Wifi, Car, Bike, Crown, Star, Info, Link, ExternalLink,
  Percent
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// Constants
// =============================================================================

const BUSINESS_TYPES = [
  { id: 'restaurant', label: 'Restaurant', icon: UtensilsCrossed, desc: 'Vollservice-Restaurant mit Bedienung' },
  { id: 'schnellrestaurant', label: 'Schnellrestaurant', icon: Pizza, desc: 'Fast Food, Imbiss, Döner, etc.' },
  { id: 'cafe', label: 'Café / Bäckerei', icon: Coffee, desc: 'Kaffee, Kuchen, Frühstück' },
  { id: 'bar', label: 'Bar / Kneipe', icon: Beer, desc: 'Getränke, Cocktails, Snacks' },
  { id: 'eisdiele', label: 'Eisdiele', icon: IceCream, desc: 'Eis, Waffeln, Desserts' },
  { id: 'lieferdienst', label: 'Reiner Lieferdienst', icon: Truck, desc: 'Nur Lieferung, kein Vor-Ort-Geschäft' },
];

const SERVICE_OPTIONS = [
  { id: 'dine_in', label: 'Vor-Ort-Verzehr', icon: UtensilsCrossed, desc: 'Gäste essen im Restaurant' },
  { id: 'takeaway', label: 'Abholung', icon: ShoppingBag, desc: 'Kunden holen Bestellung ab' },
  { id: 'delivery', label: 'Eigene Lieferung', icon: Truck, desc: 'Sie liefern selbst aus' },
  { id: 'portal_delivery', label: 'Portal-Lieferung', icon: Bike, desc: 'Lieferando, Wolt, etc. liefern' },
];

const DELIVERY_PORTALS = [
  { id: 'lieferando', label: 'Lieferando', logo: '🟠', popular: true },
  { id: 'wolt', label: 'Wolt', logo: '🔵', popular: true },
  { id: 'uber_eats', label: 'Uber Eats', logo: '🟢', popular: true },
  { id: 'food_de', label: 'food.de', logo: '🔴', popular: false },
  { id: 'mjam', label: 'Mjam', logo: '🟡', popular: false },
  { id: 'andere', label: 'Andere', logo: '📦', popular: false },
];

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Bargeld', icon: '💵' },
  { id: 'ec', label: 'EC-Karte', icon: '💳' },
  { id: 'credit', label: 'Kreditkarte', icon: '💳' },
  { id: 'paypal', label: 'PayPal', icon: '🅿️' },
  { id: 'apple_pay', label: 'Apple Pay', icon: '🍎' },
  { id: 'google_pay', label: 'Google Pay', icon: '🔷' },
];

const STEPS = [
  { id: 'business', title: 'Betrieb', icon: Building2 },
  { id: 'services', title: 'Angebot', icon: UtensilsCrossed },
  { id: 'location', title: 'Standort', icon: MapPin },
  { id: 'menu', title: 'Speisekarte', icon: ChefHat },
  { id: 'review', title: 'Kontrolle', icon: CheckCircle },
  { id: 'printer', title: 'Drucker', icon: Printer },
  { id: 'finish', title: 'Fertig', icon: Star },
];

export default function SmartOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  
  // Step 1: Business Type
  const [businessType, setBusinessType] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  
  // Step 2: Services
  const [services, setServices] = useState({
    dine_in: true,
    takeaway: false,
    delivery: false,
    portal_delivery: false
  });
  const [selectedPortals, setSelectedPortals] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState(['cash', 'ec']);
  
  // Abholerrabatt
  const [pickupDiscount, setPickupDiscount] = useState({
    enabled: false,
    percentage: 10
  });
  
  // Step 3: Location & Tables
  const [address, setAddress] = useState({ street: '', city: '', zip: '' });
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [tableCount, setTableCount] = useState(10);
  const [hasOutdoor, setHasOutdoor] = useState(false);
  const [outdoorSeats, setOutdoorSeats] = useState(0);
  const [openingHours, setOpeningHours] = useState({
    weekday_open: '11:00',
    weekday_close: '22:00',
    weekend_open: '11:00',
    weekend_close: '23:00'
  });
  
  // Step 4: Menu Import
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [menuFile, setMenuFile] = useState(null);
  const [menuUploading, setMenuUploading] = useState(false);
  const [extractionId, setExtractionId] = useState(null);
  const [extractionStatus, setExtractionStatus] = useState(null);
  const [importMode, setImportMode] = useState('file'); // file, url
  const [importUrl, setImportUrl] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  
  // Interactive AI Chat
  const [aiMessage, setAiMessage] = useState('');
  const [aiQuestions, setAiQuestions] = useState([]);
  const [aiAnswers, setAiAnswers] = useState({});
  const [aiSummary, setAiSummary] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [processingChat, setProcessingChat] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showProductReview, setShowProductReview] = useState(false);
  
  // Step 5: Review Products
  const [categories, setCategories] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Step 6: Printer Setup
  const [printerType, setPrinterType] = useState('browser'); // browser, network, usb
  const [printerName, setPrinterName] = useState('');
  const [printerIp, setPrinterIp] = useState('');
  const [printerPort, setPrinterPort] = useState(9100);
  const [paperWidth, setPaperWidth] = useState(80);
  const [printerTesting, setPrinterTesting] = useState(false);
  const [printerTestResult, setPrinterTestResult] = useState(null);
  const [printerRecommendation, setPrinterRecommendation] = useState(null);

  // Auth token
  const token = localStorage.getItem('access_token');

  // ==========================================================================
  // Load initial data
  // ==========================================================================
  
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadTenantData();
  }, [token]);

  const loadTenantData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/onboarding/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.tenant) {
        setTenant(data.tenant);
        setBusinessName(data.tenant.company_name || data.tenant.restaurant_name || '');
        setOwnerName(data.tenant.contact_name || '');
        setEmail(data.tenant.email || '');
      }
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  // ==========================================================================
  // API Functions
  // ==========================================================================

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const saveProgress = async (stepData) => {
    try {
      await fetch(`${API_BASE}/api/onboarding/smart-save`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(stepData)
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;
    
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    try {
      const response = await fetch(`${API_BASE}/api/onboarding/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      return data.logo_url;
    } catch (error) {
      console.error('Error uploading logo:', error);
      return null;
    }
  };

  const uploadAndExtractMenu = async () => {
    if (!menuFile) return;
    
    setMenuUploading(true);
    setExtractionStatus('uploading');
    setChatMessages([{ role: 'system', content: '🔄 Speisekarte wird analysiert... Das kann einen Moment dauern.' }]);
    
    const formData = new FormData();
    formData.append('file', menuFile);
    formData.append('business_type', businessType || 'restaurant');
    
    try {
      // Verwende den neuen interaktiven Endpunkt
      const response = await fetch(`${API_BASE}/api/menu-ai/extract-interactive`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Upload fehlgeschlagen');
      }
      
      const data = await response.json();
      
      // Interaktive Daten setzen
      if (data.status === 'awaiting_input') {
        setCategories(data.categories || []);
        setExtractionId(data.extraction_id);
        setAiSummary(data.summary);
        setExtractionStatus('chat_mode');
        
        // Generiere initiale Chat-Nachricht mit allen Produkten
        await generateInitialChatMessage(data.extraction_id, data.categories);
        
        toast.success(`${data.summary?.total_products || 0} Produkte erkannt!`);
      } else if (data.categories && data.categories.length > 0) {
        setCategories(data.categories);
        setExtractionStatus('completed');
        setExtractionId(data.extraction_id);
        
        const totalProducts = data.categories.reduce((sum, c) => sum + (c.products?.length || 0), 0);
        toast.success(`${data.categories.length} Kategorien mit ${totalProducts} Produkten gefunden!`);
      } else if (data.extraction_id) {
        setExtractionId(data.extraction_id);
        setExtractionStatus('processing');
        pollExtractionStatus(data.extraction_id);
      } else {
        setExtractionStatus('error');
        toast.error('Keine Produkte gefunden');
      }
      
    } catch (error) {
      console.error('Error:', error);
      setExtractionStatus('error');
      setChatMessages([{ role: 'error', content: `❌ Fehler: ${error.message}` }]);
      toast.error(error.message || 'Fehler beim Hochladen der Speisekarte');
    } finally {
      setMenuUploading(false);
    }
  };

  // Sendet die Antworten auf KI-Fragen
  const submitAiAnswers = async () => {
    if (!extractionId) return;
    
    setProcessingChat(true);
    
    // Füge User-Antworten zum Chat hinzu
    const answersText = Object.entries(aiAnswers)
      .filter(([_, v]) => v !== undefined && v !== '')
      .map(([k, v]) => {
        const question = aiQuestions.find(q => q.id === k);
        return `${question?.question || k}: ${typeof v === 'boolean' ? (v ? 'Ja' : 'Nein') : v}`;
      })
      .join('\n');
    
    if (answersText) {
      setChatMessages(prev => [...prev, { role: 'user', content: answersText }]);
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/menu-ai/chat-response`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          extraction_id: extractionId,
          answers: aiAnswers,
          free_text: userInput || null
        })
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Verarbeiten der Antworten');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.categories || []);
        setExtractionStatus('completed');
        setShowProductReview(true);
        
        // Alle Kategorien initial expandieren
        const expanded = {};
        (data.categories || []).forEach((cat, idx) => {
          expanded[idx] = true;
        });
        setExpandedCategories(expanded);
        
        setChatMessages(prev => [...prev, { 
          role: 'ai', 
          content: `✅ ${data.message}\n\nAlle Produkte wurden mit MwSt (7% für Essen, 19% für Getränke) und Upselling-Texten konfiguriert.` 
        }]);
        
        toast.success('Produkte erfolgreich konfiguriert!');
      }
      
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message);
      setChatMessages(prev => [...prev, { role: 'error', content: `❌ ${error.message}` }]);
    } finally {
      setProcessingChat(false);
      setUserInput('');
    }
  };

  // Kategorie ein-/ausklappen
  const toggleCategory = (idx) => {
    setExpandedCategories(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  // Generiere initiale Chat-Nachricht mit allen Produkten
  const generateInitialChatMessage = async (extId, cats) => {
    try {
      const response = await fetch(`${API_BASE}/api/menu-ai/generate-initial-chat?extraction_id=${extId}`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatMessages([{ role: 'ai', content: data.ai_message, isProductList: true }]);
      } else {
        // Fallback: Einfache Nachricht
        const totalProducts = cats.reduce((sum, c) => sum + (c.products?.length || 0), 0);
        setChatMessages([{
          role: 'ai',
          content: `🎉 Ich habe **${cats.length} Kategorien** mit **${totalProducts} Produkten** gefunden!\n\nSchreiben Sie mir, was Sie anpassen möchten.`,
          isProductList: false
        }]);
      }
    } catch (error) {
      console.error('Error generating initial chat:', error);
    }
  };

  // Sende Chat-Nachricht an KI
  const sendChatMessage = async () => {
    if (!userInput.trim() || !extractionId) return;
    
    const userMessage = userInput.trim();
    setUserInput('');
    setProcessingChat(true);
    
    // User-Nachricht hinzufügen
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    // Typing indicator
    setChatMessages(prev => [...prev, { role: 'typing', content: '...' }]);
    
    try {
      const response = await fetch(`${API_BASE}/api/menu-ai/smart-chat`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          extraction_id: extractionId,
          message: userMessage
        })
      });
      
      // Remove typing indicator
      setChatMessages(prev => prev.filter(m => m.role !== 'typing'));
      
      if (!response.ok) {
        throw new Error('Chat-Fehler');
      }
      
      const data = await response.json();
      
      // KI-Antwort hinzufügen
      setChatMessages(prev => [...prev, { 
        role: 'ai', 
        content: data.ai_message,
        changes: data.changes_made,
        followUp: data.follow_up_questions
      }]);
      
      // Kategorien aktualisieren
      if (data.categories) {
        setCategories(data.categories);
      }
      
      // Änderungen als Toast anzeigen
      if (data.changes_made && data.changes_made.length > 0) {
        data.changes_made.forEach(change => {
          toast.success(change, { duration: 4000 });
        });
      }
      
      // Status prüfen
      if (data.status === 'completed') {
        setExtractionStatus('completed');
        toast.success('🎉 Konfiguration abgeschlossen!', { duration: 5000 });
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => prev.filter(m => m.role !== 'typing'));
      setChatMessages(prev => [...prev, { 
        role: 'error', 
        content: '❌ Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.' 
      }]);
    } finally {
      setProcessingChat(false);
    }
  };

  // Quick-Action Buttons
  const sendQuickAction = (action) => {
    setUserInput(action);
    // Auto-send after setting
    setTimeout(() => {
      const input = document.querySelector('[data-testid="chat-input"]');
      if (input) {
        input.form?.requestSubmit();
      }
    }, 100);
  };

  // Antwort auf eine einzelne Frage setzen
  const handleAnswerChange = (questionId, value) => {
    setAiAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // URL-Import Funktion (Lieferando, Wolt, Website)
  const importFromUrl = async () => {
    if (!importUrl.trim()) {
      toast.error('Bitte geben Sie eine URL ein');
      return;
    }
    
    setUrlLoading(true);
    setExtractionStatus('processing');
    
    try {
      const response = await fetch(`${API_BASE}/api/menu-ai/extract-from-url`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          url: importUrl.trim(),
          source_type: 'auto'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = errorData.detail || 'Import fehlgeschlagen';
        
        // Spezifische Fehlermeldungen
        if (errorMsg.includes('Captcha') || errorMsg.includes('Sicherheit')) {
          setExtractionStatus('captcha');
          toast.error('Website blockiert automatische Zugriffe. Bitte laden Sie ein Foto/PDF hoch.', {
            duration: 6000
          });
        } else if (errorMsg.includes('keine Speisekarte') || errorMsg.includes('nicht gefunden')) {
          setExtractionStatus('not_found');
          toast.error('Keine Speisekarte auf dieser Seite gefunden.', {
            duration: 5000
          });
        } else {
          setExtractionStatus('error');
          toast.error(errorMsg);
        }
        return;
      }
      
      const data = await response.json();
      
      if (data.status === 'completed' && data.categories?.length > 0) {
        setCategories(data.categories);
        setExtractionStatus('completed');
        setExtractionId(data.extraction_id);
        
        const totalProducts = data.categories.reduce((sum, c) => sum + (c.products?.length || 0), 0);
        toast.success(`${data.categories.length} Kategorien mit ${totalProducts} Produkten importiert!`);
      } else {
        setExtractionStatus('error');
        toast.error('Keine Produkte gefunden. Bitte prüfen Sie die URL.');
      }
      
    } catch (error) {
      console.error('URL Import Error:', error);
      setExtractionStatus('error');
      toast.error(error.message || 'Fehler beim Importieren von der URL');
    } finally {
      setUrlLoading(false);
    }
  };

  const pollExtractionStatus = async (extId) => {
    const maxAttempts = 30;
    let attempts = 0;
    
    const poll = async () => {
      attempts++;
      try {
        const response = await fetch(`${API_BASE}/api/menu-ai/extraction/${extId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.status === 'completed') {
          setExtractionStatus('completed');
          setCategories(data.categories || []);
          toast.success(`${data.categories?.length || 0} Kategorien erkannt!`);
        } else if (data.status === 'failed') {
          setExtractionStatus('error');
          toast.error('KI-Extraktion fehlgeschlagen');
        } else if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setExtractionStatus('timeout');
          toast.error('Extraktion dauert zu lange');
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      }
    };
    
    poll();
  };

  // Drucker-Empfehlung laden
  const loadPrinterRecommendation = async () => {
    if (!businessType) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/smart-onboarding/printer-recommendation?business_type=${businessType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPrinterRecommendation(data);
    } catch (error) {
      console.error('Error loading printer recommendation:', error);
    }
  };

  // Drucker testen
  const testPrinter = async () => {
    setPrinterTesting(true);
    setPrinterTestResult(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/smart-onboarding/test-printer`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          printer_type: printerType,
          printer_name: printerName,
          printer_ip: printerIp,
          printer_port: printerPort,
          paper_width: paperWidth
        })
      });
      const data = await response.json();
      setPrinterTestResult(data);
      
      if (data.success) {
        toast.success(data.message || 'Testdruck erfolgreich!');
      } else {
        toast.error(data.message || 'Drucker-Test fehlgeschlagen');
      }
    } catch (error) {
      console.error('Printer test error:', error);
      toast.error('Fehler beim Drucker-Test');
      setPrinterTestResult({ success: false, message: 'Verbindungsfehler' });
    } finally {
      setPrinterTesting(false);
    }
  };

  const createTablesAndFinish = async () => {
    setLoading(true);
    
    try {
      // 1. Upload logo if exists
      let logoUrl = null;
      if (logoFile) {
        logoUrl = await uploadLogo();
      }
      
      // 2. Collect approved products
      const approvedProducts = [];
      categories.forEach(cat => {
        if (cat.approved !== false) {
          cat.products?.forEach(prod => {
            if (prod.approved !== false) {
              approvedProducts.push({
                ...prod,
                category: cat.name
              });
            }
          });
        }
      });
      
      // 3. Call Smart Onboarding Complete API
      const response = await fetch(`${API_BASE}/api/smart-onboarding/complete`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          business: {
            business_type: businessType,
            business_name: businessName,
            owner_name: ownerName
          },
          services: {
            dine_in: services.dine_in,
            takeaway: services.takeaway,
            delivery: services.delivery,
            portal_delivery: services.portal_delivery,
            delivery_portals: selectedPortals,
            payment_methods: paymentMethods,
            pickup_discount: pickupDiscount
          },
          location: {
            street: address.street,
            zip: address.zip,
            city: address.city,
            phone,
            email,
            table_count: tableCount,
            has_outdoor: hasOutdoor,
            outdoor_seats: outdoorSeats,
            opening_hours: openingHours
          },
          printer: {
            printer_type: printerType,
            printer_name: printerName,
            printer_ip: printerIp,
            printer_port: printerPort,
            paper_width: paperWidth
          },
          products: approvedProducts
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Einrichtung abgeschlossen!');
        setCurrentStep(6); // Finish step (now index 6 with printer step)
      } else {
        toast.error(result.detail || 'Fehler bei der Einrichtung');
      }
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler bei der Einrichtung');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleMenuChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMenuFile(file);
    }
  };

  const toggleService = (serviceId) => {
    setServices(prev => ({ ...prev, [serviceId]: !prev[serviceId] }));
  };

  const togglePortal = (portalId) => {
    setSelectedPortals(prev => 
      prev.includes(portalId) 
        ? prev.filter(p => p !== portalId)
        : [...prev, portalId]
    );
  };

  const togglePayment = (methodId) => {
    setPaymentMethods(prev =>
      prev.includes(methodId)
        ? prev.filter(p => p !== methodId)
        : [...prev, methodId]
    );
  };

  const toggleCategoryApproval = (catIndex) => {
    setCategories(prev => {
      const updated = [...prev];
      updated[catIndex] = { ...updated[catIndex], approved: !updated[catIndex].approved };
      return updated;
    });
  };

  const toggleProductApproval = (catIndex, prodIndex) => {
    setCategories(prev => {
      const updated = [...prev];
      const products = [...updated[catIndex].products];
      products[prodIndex] = { ...products[prodIndex], approved: !products[prodIndex].approved };
      updated[catIndex] = { ...updated[catIndex], products };
      return updated;
    });
  };

  const updateProduct = (catIndex, prodIndex, field, value) => {
    setCategories(prev => {
      const updated = [...prev];
      const products = [...updated[catIndex].products];
      products[prodIndex] = { ...products[prodIndex], [field]: value };
      updated[catIndex] = { ...updated[catIndex], products };
      return updated;
    });
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
      // Lade Drucker-Empfehlung wenn wir zum Drucker-Step kommen
      if (currentStep === 4) { // nach Review kommt Printer
        loadPrinterRecommendation();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return businessType && businessName;
      case 1: return Object.values(services).some(v => v);
      case 2: return tableCount > 0;
      case 3: return true; // Menu is optional
      case 4: return true; // Review
      case 5: return true; // Printer (browser is always valid)
      default: return true;
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  // ==========================================================================
  // Render Steps
  // ==========================================================================

  const renderStep1_Business = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Was für ein Betrieb sind Sie?</h2>
        <p className="text-gray-400">Wählen Sie die Art Ihres Geschäfts</p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {BUSINESS_TYPES.map((type) => {
          const Icon = type.icon;
          const isSelected = businessType === type.id;
          return (
            <Card
              key={type.id}
              className={`cursor-pointer transition-all hover:scale-105 ${
                isSelected 
                  ? 'bg-blue-600 border-blue-400 ring-2 ring-blue-400' 
                  : 'bg-gray-800 border-gray-700 hover:border-gray-600'
              }`}
              onClick={() => setBusinessType(type.id)}
            >
              <CardContent className="pt-6 text-center">
                <Icon className={`w-10 h-10 mx-auto mb-3 ${isSelected ? 'text-white' : 'text-gray-400'}`} />
                <h3 className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-200'}`}>{type.label}</h3>
                <p className={`text-xs mt-1 ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>{type.desc}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <div className="space-y-2">
          <Label className="text-white">Name Ihres Betriebs *</Label>
          <Input
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="z.B. Pizzeria Milano"
            className="bg-gray-800 border-gray-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white">Ihr Name (Inhaber)</Label>
          <Input
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="z.B. Mario Rossi"
            className="bg-gray-800 border-gray-700 text-white"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2_Services = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Was bieten Sie an?</h2>
        <p className="text-gray-400">Wählen Sie alle zutreffenden Optionen</p>
      </div>
      
      {/* Service Types */}
      <div className="grid grid-cols-2 gap-4">
        {SERVICE_OPTIONS.map((service) => {
          const Icon = service.icon;
          const isSelected = services[service.id];
          return (
            <Card
              key={service.id}
              className={`cursor-pointer transition-all ${
                isSelected 
                  ? 'bg-green-600/20 border-green-500' 
                  : 'bg-gray-800 border-gray-700 hover:border-gray-600'
              }`}
              onClick={() => toggleService(service.id)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isSelected ? 'bg-green-500/20' : 'bg-gray-700'}`}>
                    <Icon className={`w-6 h-6 ${isSelected ? 'text-green-400' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-white">{service.label}</h3>
                      <Switch checked={isSelected} />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{service.desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Abholerrabatt - erscheint wenn Abholung ausgewählt */}
      {services.takeaway && (
        <Card 
          className={`mt-6 cursor-pointer transition-all ${
            pickupDiscount.enabled 
              ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500' 
              : 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30 hover:border-yellow-500/50'
          }`}
          onClick={() => !pickupDiscount.enabled && setPickupDiscount(prev => ({ ...prev, enabled: true }))}
        >
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${pickupDiscount.enabled ? 'bg-yellow-500/30' : 'bg-yellow-500/20'}`}>
                <Percent className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-white">🎁 Abholerrabatt anbieten?</h3>
                    <Switch 
                      checked={pickupDiscount.enabled}
                      onCheckedChange={(checked) => setPickupDiscount(prev => ({ ...prev, enabled: checked }))}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    Kunden, die ihre Bestellung abholen, erhalten automatisch einen Rabatt
                  </p>
                </div>
                
                {pickupDiscount.enabled && (
                  <div className="bg-gray-800/50 rounded-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <Label className="text-gray-300">Wie viel Prozent Rabatt?</Label>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex gap-2">
                        {[5, 10, 15, 20].map((pct) => (
                          <Button
                            key={pct}
                            type="button"
                            variant={pickupDiscount.percentage === pct ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPickupDiscount(prev => ({ ...prev, percentage: pct }))}
                            className={pickupDiscount.percentage === pct ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                          >
                            {pct}%
                          </Button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="50"
                          value={pickupDiscount.percentage}
                          onChange={(e) => setPickupDiscount(prev => ({ ...prev, percentage: parseInt(e.target.value) || 10 }))}
                          className="w-20 bg-gray-900 border-gray-600"
                        />
                        <span className="text-gray-400">%</span>
                      </div>
                    </div>
                    <p className="text-xs text-yellow-400/80">
                      💡 Tipp: 10% Abholerrabatt ist branchenüblich und motiviert Kunden zur Selbstabholung
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Delivery Portals */}
      {(services.portal_delivery || services.delivery) && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-400" />
            Mit welchen Portalen arbeiten Sie?
          </h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {DELIVERY_PORTALS.map((portal) => {
              const isSelected = selectedPortals.includes(portal.id);
              return (
                <div
                  key={portal.id}
                  className={`p-3 rounded-lg cursor-pointer text-center transition-all ${
                    isSelected 
                      ? 'bg-blue-600/20 border border-blue-500' 
                      : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                  }`}
                  onClick={() => togglePortal(portal.id)}
                >
                  <span className="text-2xl">{portal.logo}</span>
                  <p className={`text-xs mt-1 ${isSelected ? 'text-blue-300' : 'text-gray-400'}`}>
                    {portal.label}
                  </p>
                  {portal.popular && (
                    <Badge variant="outline" className="text-xs mt-1 text-yellow-500 border-yellow-500/50">
                      Beliebt
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Payment Methods */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-green-400" />
          Welche Zahlungsarten akzeptieren Sie?
        </h3>
        <div className="flex flex-wrap gap-3">
          {PAYMENT_METHODS.map((method) => {
            const isSelected = paymentMethods.includes(method.id);
            return (
              <div
                key={method.id}
                className={`px-4 py-2 rounded-full cursor-pointer transition-all flex items-center gap-2 ${
                  isSelected 
                    ? 'bg-green-600/20 border border-green-500 text-green-300' 
                    : 'bg-gray-800 border border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
                onClick={() => togglePayment(method.id)}
              >
                <span>{method.icon}</span>
                <span className="text-sm">{method.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderStep3_Location = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Standort & Einrichtung</h2>
        <p className="text-gray-400">Informationen zu Ihrem Standort</p>
      </div>
      
      {/* Contact Info */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-400" />
            Adresse & Kontakt
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Straße & Hausnummer</Label>
              <Input
                value={address.street}
                onChange={(e) => setAddress({...address, street: e.target.value})}
                placeholder="Hauptstraße 123"
                className="bg-gray-900 border-gray-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label className="text-gray-300">PLZ</Label>
                <Input
                  value={address.zip}
                  onChange={(e) => setAddress({...address, zip: e.target.value})}
                  placeholder="12345"
                  className="bg-gray-900 border-gray-700"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Stadt</Label>
                <Input
                  value={address.city}
                  onChange={(e) => setAddress({...address, city: e.target.value})}
                  placeholder="Berlin"
                  className="bg-gray-900 border-gray-700"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Telefon</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+49 30 12345678"
                className="bg-gray-900 border-gray-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">E-Mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@restaurant.de"
                className="bg-gray-900 border-gray-700"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tables */}
      {services.dine_in && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              Tische & Sitzplätze
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-gray-300">Anzahl Tische (innen)</Label>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setTableCount(Math.max(1, tableCount - 1))}
                    className="border-gray-600"
                  >
                    -
                  </Button>
                  <span className="text-3xl font-bold text-white w-16 text-center">{tableCount}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setTableCount(tableCount + 1)}
                    className="border-gray-600"
                  >
                    +
                  </Button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Sun className="w-4 h-4 text-yellow-400" />
                    Außenterrasse vorhanden?
                  </Label>
                  <Switch
                    checked={hasOutdoor}
                    onCheckedChange={setHasOutdoor}
                  />
                </div>
                
                {hasOutdoor && (
                  <div className="space-y-2 mt-4">
                    <Label className="text-gray-400 text-sm">Außenplätze</Label>
                    <Input
                      type="number"
                      value={outdoorSeats}
                      onChange={(e) => setOutdoorSeats(parseInt(e.target.value) || 0)}
                      placeholder="z.B. 20"
                      className="bg-gray-900 border-gray-700"
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Opening Hours */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-400" />
            Öffnungszeiten
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label className="text-gray-300">Montag - Freitag</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={openingHours.weekday_open}
                  onChange={(e) => setOpeningHours({...openingHours, weekday_open: e.target.value})}
                  className="bg-gray-900 border-gray-700"
                />
                <span className="text-gray-500">bis</span>
                <Input
                  type="time"
                  value={openingHours.weekday_close}
                  onChange={(e) => setOpeningHours({...openingHours, weekday_close: e.target.value})}
                  className="bg-gray-900 border-gray-700"
                />
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-gray-300">Samstag - Sonntag</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={openingHours.weekend_open}
                  onChange={(e) => setOpeningHours({...openingHours, weekend_open: e.target.value})}
                  className="bg-gray-900 border-gray-700"
                />
                <span className="text-gray-500">bis</span>
                <Input
                  type="time"
                  value={openingHours.weekend_close}
                  onChange={(e) => setOpeningHours({...openingHours, weekend_close: e.target.value})}
                  className="bg-gray-900 border-gray-700"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep4_Menu = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">🤖 KI-Menü-Import</h2>
        <p className="text-gray-400">Laden Sie Ihre Speisekarte hoch - die KI erledigt den Rest</p>
      </div>
      
      {/* Logo Upload */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Ihr Logo</CardTitle>
          <CardDescription>Optional - JPG, PNG oder WebP</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:border-blue-500 ${
              logoPreview ? 'border-green-500 bg-green-500/10' : 'border-gray-600'
            }`}
            onClick={() => document.getElementById('logo-input').click()}
          >
            {logoPreview ? (
              <div className="space-y-4">
                <img src={logoPreview} alt="Logo" className="max-h-32 mx-auto rounded-lg" />
                <p className="text-green-400 text-sm">✓ Logo ausgewählt</p>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">Klicken zum Hochladen</p>
              </>
            )}
            <input
              id="logo-input"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
            />
          </div>
        </CardContent>
      </Card>
      
      {/* File Upload - nur wenn noch keine Extraktion läuft */}
      {extractionStatus !== 'awaiting_input' && extractionStatus !== 'completed' && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              Speisekarte hochladen
            </CardTitle>
            <CardDescription>PDF oder Foto Ihrer Speisekarte</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:border-blue-500 ${
                menuFile ? 'border-green-500 bg-green-500/10' : 'border-gray-600'
              }`}
              onClick={() => !menuUploading && document.getElementById('menu-input').click()}
              data-testid="menu-file-upload"
            >
              {menuUploading ? (
                <div className="space-y-4">
                  <RefreshCw className="w-12 h-12 mx-auto text-blue-400 animate-spin" />
                  <p className="text-blue-400">🤖 KI analysiert Speisekarte...</p>
                  <p className="text-gray-500 text-sm">Das kann einen Moment dauern</p>
                </div>
              ) : menuFile ? (
                <div className="space-y-4">
                  <ChefHat className="w-12 h-12 mx-auto text-blue-400" />
                  <p className="text-blue-400">{menuFile.name}</p>
                  <Button 
                    onClick={(e) => { e.stopPropagation(); uploadAndExtractMenu(); }} 
                    data-testid="start-ai-analysis-btn"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    KI-Analyse starten
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                  <p className="text-gray-400">PDF oder Foto hochladen</p>
                  <p className="text-gray-600 text-xs mt-2">Die KI erkennt automatisch alle Produkte, Größen, Preise und Allergene</p>
                </>
              )}
              <input
                id="menu-input"
                type="file"
                accept="application/pdf,image/*"
                onChange={handleMenuChange}
                className="hidden"
              />
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* ========== ECHTER KI-CHAT INTERFACE ========== */}
      {(extractionStatus === 'chat_mode' || extractionStatus === 'awaiting_input') && (
        <Card className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-purple-500/50 shadow-xl shadow-purple-500/10">
          <CardHeader className="border-b border-gray-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white">🤖 KI-Menü-Assistent</CardTitle>
                  <CardDescription>Schreiben Sie mir, was Sie anpassen möchten</CardDescription>
                </div>
              </div>
              {aiSummary && (
                <div className="flex gap-2">
                  <Badge className="bg-blue-500/20 text-blue-400">{aiSummary.total_categories} Kategorien</Badge>
                  <Badge className="bg-green-500/20 text-green-400">{aiSummary.total_products} Produkte</Badge>
                </div>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {/* Chat Messages Container */}
            <div 
              className="h-[450px] overflow-y-auto p-4 space-y-4 bg-gray-900/30"
              style={{ scrollBehavior: 'smooth' }}
              ref={(el) => el && (el.scrollTop = el.scrollHeight)}
            >
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'typing' ? (
                    <div className="bg-gray-800 rounded-2xl px-4 py-3 flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                        <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                        <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                      </div>
                      <span className="text-gray-400 text-sm">KI denkt nach...</span>
                    </div>
                  ) : msg.role === 'ai' ? (
                    <div className="max-w-[85%] space-y-2">
                      <div className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-2xl rounded-tl-sm p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-1.5 bg-purple-500/30 rounded-lg flex-shrink-0">
                            <Sparkles className="w-4 h-4 text-purple-300" />
                          </div>
                          <div className="text-gray-200 text-sm whitespace-pre-wrap prose prose-invert prose-sm max-w-none">
                            {msg.content.split('\n').map((line, i) => {
                              // Render markdown-like formatting
                              if (line.startsWith('###')) {
                                return <h3 key={i} className="text-lg font-bold text-white mt-4 mb-2">{line.replace(/^###\s*/, '')}</h3>;
                              }
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return <p key={i} className="font-bold text-white">{line.replace(/\*\*/g, '')}</p>;
                              }
                              if (line.startsWith('- ')) {
                                return <p key={i} className="ml-2 text-gray-300">{line}</p>;
                              }
                              if (line === '---') {
                                return <hr key={i} className="border-gray-600 my-3" />;
                              }
                              return <p key={i}>{line}</p>;
                            })}
                          </div>
                        </div>
                      </div>
                      
                      {/* Änderungen anzeigen */}
                      {msg.changes && msg.changes.length > 0 && (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 ml-8">
                          <p className="text-green-400 text-xs font-medium mb-2">✅ Änderungen vorgenommen:</p>
                          {msg.changes.map((change, i) => (
                            <p key={i} className="text-green-300 text-xs">{change}</p>
                          ))}
                        </div>
                      )}
                      
                      {/* Follow-up Fragen */}
                      {msg.followUp && msg.followUp.length > 0 && (
                        <div className="flex flex-wrap gap-2 ml-8">
                          {msg.followUp.map((q, i) => (
                            <Button
                              key={i}
                              variant="outline"
                              size="sm"
                              className="text-xs border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                              onClick={() => setUserInput(q)}
                            >
                              {q}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : msg.role === 'user' ? (
                    <div className="max-w-[70%]">
                      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl rounded-tr-sm p-4">
                        <p className="text-white text-sm">{msg.content}</p>
                      </div>
                    </div>
                  ) : msg.role === 'error' ? (
                    <div className="max-w-[80%]">
                      <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-4">
                        <p className="text-red-300 text-sm">{msg.content}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full text-center">
                      <p className="text-gray-500 text-sm">{msg.content}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Quick Actions */}
            <div className="p-3 border-t border-gray-700/50 bg-gray-800/50">
              <p className="text-xs text-gray-500 mb-2">💡 Schnellaktionen:</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                  onClick={() => setUserInput('Ja, wir haben Menüs für 5,90€ mit Pommes und Cola')}
                >
                  🍟 Menüs aktivieren
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-green-500/50 text-green-400 hover:bg-green-500/20"
                  onClick={() => setUserInput('Extra Käse kostet 1,50€, Extra Bacon 2,00€')}
                >
                  ➕ Extras definieren
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                  onClick={() => setUserInput('Bei allen Burgern gibt es Mitarbeiter-Tipp: Darf es ein Menü sein?')}
                >
                  💬 Upselling-Text
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                  onClick={() => setUserInput('Alles korrekt, ich bin fertig')}
                >
                  ✅ Fertig
                </Button>
              </div>
            </div>
            
            {/* Chat Input */}
            <form 
              onSubmit={(e) => { e.preventDefault(); sendChatMessage(); }}
              className="p-4 border-t border-gray-700/50 bg-gray-800/80"
            >
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Textarea
                    data-testid="chat-input"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Schreiben Sie hier... z.B. 'Ja, Menüs kosten 5,90€ extra'"
                    className="bg-gray-900 border-gray-600 text-white placeholder-gray-500 resize-none pr-12 min-h-[50px]"
                    rows={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendChatMessage();
                      }
                    }}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={processingChat || !userInput.trim()}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-6"
                >
                  {processingChat ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <ArrowRight className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      
      {/* ========== DETAILLIERTE PRODUKTLISTE ========== */}
      {extractionStatus === 'completed' && categories.length > 0 && (
        <div className="space-y-4">
          {/* Erfolgs-Header */}
          <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-green-400 text-lg font-bold">
                      🎉 {categories.reduce((sum, c) => sum + (c.products?.length || 0), 0)} Produkte erkannt!
                    </p>
                    <p className="text-gray-400 text-sm">
                      {categories.length} Kategorien • Bitte kontrollieren Sie die Angaben
                    </p>
                  </div>
                </div>
                <Badge className="bg-purple-500/20 text-purple-400">
                  MwSt automatisch gesetzt
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          {/* KI-Zusammenfassung */}
          {aiSummary && (
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <div className="space-y-2 text-sm">
                    <p className="text-purple-300 font-medium">🤖 Das habe ich erkannt:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                      <div className="bg-gray-800/50 rounded p-2 text-center">
                        <p className="text-2xl font-bold text-white">{aiSummary.total_categories}</p>
                        <p className="text-xs text-gray-400">Kategorien</p>
                      </div>
                      <div className="bg-gray-800/50 rounded p-2 text-center">
                        <p className="text-2xl font-bold text-white">{aiSummary.total_products}</p>
                        <p className="text-xs text-gray-400">Produkte</p>
                      </div>
                      <div className="bg-gray-800/50 rounded p-2 text-center">
                        <p className="text-2xl font-bold text-blue-400">{aiSummary.products_with_variants}</p>
                        <p className="text-xs text-gray-400">Mit Größen</p>
                      </div>
                      <div className="bg-gray-800/50 rounded p-2 text-center">
                        <p className="text-2xl font-bold text-amber-400">{aiSummary.recognized_allergens?.length || 0}</p>
                        <p className="text-xs text-gray-400">Allergene</p>
                      </div>
                    </div>
                    
                    {/* Erkannte Größen */}
                    {aiSummary.recognized_sizes?.length > 0 && (
                      <div className="mt-3 p-2 bg-gray-800/30 rounded">
                        <p className="text-gray-400 text-xs mb-1">📐 Erkannte Größen-Varianten:</p>
                        <div className="flex flex-wrap gap-2">
                          {aiSummary.recognized_sizes.map((s, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {s.category}: {s.sizes?.slice(0, 3).join(', ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Menü-Info wenn aktiviert */}
                    {aiAnswers?.has_menus === 'yes' && (
                      <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
                        <p className="text-yellow-400 text-xs">
                          🍟 <strong>Menüs aktiviert:</strong> +{aiAnswers.menu_price?.toFixed(2) || '5.90'}€ für {aiAnswers.menu_items || 'Pommes und Getränk'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Kategorien mit Produkten */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium">📋 Alle Produkte kontrollieren:</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const allExpanded = Object.values(expandedCategories).every(v => v);
                  const newState = {};
                  categories.forEach((_, idx) => { newState[idx] = !allExpanded; });
                  setExpandedCategories(newState);
                }}
              >
                {Object.values(expandedCategories).every(v => v) ? 'Alle einklappen' : 'Alle aufklappen'}
              </Button>
            </div>
            
            {categories.map((category, catIdx) => (
              <Card key={catIdx} className="bg-gray-800/50 border-gray-700">
                {/* Kategorie-Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-700/30 transition-colors"
                  onClick={() => toggleCategory(catIdx)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      category.name?.toLowerCase().includes('burger') ? 'bg-orange-500/20' :
                      category.name?.toLowerCase().includes('pizza') ? 'bg-red-500/20' :
                      category.name?.toLowerCase().includes('drink') || category.name?.toLowerCase().includes('getränk') ? 'bg-blue-500/20' :
                      category.name?.toLowerCase().includes('pasta') ? 'bg-yellow-500/20' :
                      category.name?.toLowerCase().includes('salat') || category.name?.toLowerCase().includes('salad') ? 'bg-green-500/20' :
                      'bg-gray-700'
                    }`}>
                      {category.name?.toLowerCase().includes('burger') ? '🍔' :
                       category.name?.toLowerCase().includes('pizza') ? '🍕' :
                       category.name?.toLowerCase().includes('drink') || category.name?.toLowerCase().includes('getränk') ? '🥤' :
                       category.name?.toLowerCase().includes('pasta') ? '🍝' :
                       category.name?.toLowerCase().includes('salat') || category.name?.toLowerCase().includes('salad') ? '🥗' :
                       category.name?.toLowerCase().includes('wrap') ? '🌯' :
                       category.name?.toLowerCase().includes('dessert') || category.name?.toLowerCase().includes('eis') ? '🍨' :
                       category.name?.toLowerCase().includes('kids') || category.name?.toLowerCase().includes('kinder') ? '👶' :
                       category.name?.toLowerCase().includes('finger') || category.name?.toLowerCase().includes('snack') ? '🍟' :
                       category.name?.toLowerCase().includes('dip') || category.name?.toLowerCase().includes('sauce') ? '🫙' :
                       '📦'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{category.name}</p>
                      <p className="text-gray-500 text-sm">{category.products?.length || 0} Produkte</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      category.products?.some(p => p.vat_rate === 19) 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : 'bg-green-500/20 text-green-400'
                    }>
                      {category.products?.some(p => p.vat_rate === 19) ? '19% MwSt' : '7% MwSt'}
                    </Badge>
                    <ArrowRight className={`w-5 h-5 text-gray-400 transition-transform ${expandedCategories[catIdx] ? 'rotate-90' : ''}`} />
                  </div>
                </div>
                
                {/* Produkte */}
                {expandedCategories[catIdx] && (
                  <div className="border-t border-gray-700">
                    {category.products?.map((product, prodIdx) => (
                      <div 
                        key={prodIdx} 
                        className={`p-4 border-b border-gray-700/50 last:border-b-0 ${prodIdx % 2 === 0 ? 'bg-gray-800/30' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          {/* Produkt-Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-medium">{product.name}</p>
                              {product.dietary_info?.includes('vegetarisch') && (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">🥬 Vegetarisch</Badge>
                              )}
                              {product.dietary_info?.includes('vegan') && (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">🌱 Vegan</Badge>
                              )}
                              {product.allergens?.length > 0 && (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                  ⚠️ {product.allergens.join(', ')}
                                </Badge>
                              )}
                            </div>
                            
                            {product.description && (
                              <p className="text-gray-500 text-sm mt-1 truncate">{product.description}</p>
                            )}
                            
                            {/* Größen-Varianten */}
                            {product.variants && product.variants.length > 0 ? (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {product.variants.map((variant, vIdx) => (
                                  <div 
                                    key={vIdx} 
                                    className="bg-gray-700/50 rounded px-3 py-1 text-sm flex items-center gap-2"
                                  >
                                    <span className="text-gray-400">{variant.name}</span>
                                    <span className="text-white font-medium">{variant.price?.toFixed(2)}€</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="mt-2">
                                <span className="bg-gray-700/50 rounded px-3 py-1 text-sm text-white font-medium">
                                  {product.base_price?.toFixed(2)}€
                                </span>
                              </div>
                            )}
                            
                            {/* Menü-Info */}
                            {product.menu_info?.available && (
                              <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs">
                                <span className="text-yellow-400">
                                  🍟 <strong>Menü möglich:</strong> +{product.menu_info.price_addition?.toFixed(2)}€ für {product.menu_info.includes}
                                </span>
                              </div>
                            )}
                            
                            {/* Upselling-Text */}
                            {product.staff_prompt && (
                              <div className="mt-2 p-2 bg-purple-500/10 border border-purple-500/20 rounded text-xs">
                                <span className="text-purple-400">
                                  💡 <strong>Mitarbeiter-Tipp:</strong> {product.staff_prompt.split('\n')[0]}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Preis & MwSt */}
                          <div className="text-right flex-shrink-0">
                            <p className="text-white font-bold text-lg">
                              ab {product.base_price?.toFixed(2)}€
                            </p>
                            <p className="text-gray-500 text-xs">
                              inkl. {product.vat_rate || 7}% MwSt
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
          
          {/* Bestätigung */}
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-blue-300 font-medium">Alles korrekt?</p>
                  <p className="text-gray-400 text-sm">
                    Klicken Sie auf "Weiter", um die Produkte zu übernehmen. Sie können später alles bearbeiten.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Info Box - Skip Option */}
      {extractionStatus !== 'completed' && (
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-blue-400 flex-shrink-0" />
              <div>
                <p className="text-blue-300 font-medium">Keine Speisekarte zur Hand?</p>
                <p className="text-gray-400 text-sm mt-1">
                  Kein Problem! Sie können diesen Schritt überspringen und Produkte später manuell anlegen.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderStep5_Review = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Produkte kontrollieren & freigeben</h2>
        <p className="text-gray-400">Prüfen Sie die erkannten Produkte und passen Sie sie bei Bedarf an</p>
      </div>
      
      {categories.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="py-12 text-center">
            <ChefHat className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">Keine Produkte zum Überprüfen</p>
            <p className="text-gray-600 text-sm mt-2">
              Sie können Produkte später unter &quot;Produkte&quot; manuell anlegen
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Summary */}
          <div className="flex items-center justify-between bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-4">
              <Badge className="bg-green-500/20 text-green-400">
                {categories.filter(c => c.approved !== false).length} Kategorien
              </Badge>
              <Badge className="bg-blue-500/20 text-blue-400">
                {categories.reduce((sum, c) => sum + (c.products?.filter(p => p.approved !== false).length || 0), 0)} Produkte
              </Badge>
            </div>
            <p className="text-gray-500 text-sm">Klicken Sie auf ✗ um Einträge zu deaktivieren</p>
          </div>
          
          {/* Categories Accordion */}
          <Accordion type="multiple" className="space-y-2">
            {categories.map((category, catIndex) => (
              <AccordionItem
                key={catIndex}
                value={`cat-${catIndex}`}
                className={`border rounded-lg overflow-hidden ${
                  category.approved === false 
                    ? 'border-red-500/50 bg-red-500/5' 
                    : 'border-gray-700 bg-gray-800'
                }`}
              >
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${
                          category.approved === false 
                            ? 'text-red-400 hover:text-red-300' 
                            : 'text-green-400 hover:text-green-300'
                        }`}
                        onClick={(e) => { e.stopPropagation(); toggleCategoryApproval(catIndex); }}
                      >
                        {category.approved === false ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                      </Button>
                      <span className={`font-semibold ${category.approved === false ? 'text-gray-500 line-through' : 'text-white'}`}>
                        {category.name}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-gray-400">
                      {category.products?.length || 0} Produkte
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-2">
                    {category.products?.map((product, prodIndex) => (
                      <div
                        key={prodIndex}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          product.approved === false 
                            ? 'bg-red-500/10 border border-red-500/30' 
                            : 'bg-gray-900/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 ${
                              product.approved === false 
                                ? 'text-red-400' 
                                : 'text-green-400'
                            }`}
                            onClick={() => toggleProductApproval(catIndex, prodIndex)}
                          >
                            {product.approved === false ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <div>
                            <p className={`font-medium ${product.approved === false ? 'text-gray-500 line-through' : 'text-white'}`}>
                              {product.name}
                            </p>
                            {product.description && (
                              <p className="text-gray-500 text-xs">{product.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input
                            type="number"
                            step="0.01"
                            value={product.price || ''}
                            onChange={(e) => updateProduct(catIndex, prodIndex, 'price', parseFloat(e.target.value))}
                            className="w-24 bg-gray-800 border-gray-700 text-right"
                            disabled={product.approved === false}
                          />
                          <span className="text-gray-500">€</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );

  const renderStep6_Printer = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Drucker einrichten</h2>
        <p className="text-gray-400">Wählen Sie wie Sie Bons drucken möchten</p>
      </div>
      
      {/* KI-Empfehlung */}
      {printerRecommendation && (
        <Card className={`border ${printerRecommendation.recommendation?.need_printer ? 'bg-blue-500/10 border-blue-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${printerRecommendation.recommendation?.need_printer ? 'bg-blue-500/20' : 'bg-green-500/20'}`}>
                <Sparkles className={`w-6 h-6 ${printerRecommendation.recommendation?.need_printer ? 'text-blue-400' : 'text-green-400'}`} />
              </div>
              <div>
                <p className="text-white font-medium">KI-Empfehlung für {BUSINESS_TYPES.find(t => t.id === businessType)?.label || 'Ihren Betrieb'}</p>
                <p className="text-gray-400 text-sm mt-1">{printerRecommendation.recommendation?.reason}</p>
                {printerRecommendation.recommendation?.recommended_model && (
                  <Badge className="mt-2 bg-blue-500/20 text-blue-300 border-blue-500/30">
                    Empfohlen: {printerRecommendation.recommendation.recommended_model}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Drucker-Typ Auswahl */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Browser-Druck */}
        <Card
          data-testid="printer-type-browser"
          className={`cursor-pointer transition-all ${
            printerType === 'browser' 
              ? 'bg-green-600/20 border-green-500 ring-2 ring-green-500' 
              : 'bg-gray-800 border-gray-700 hover:border-gray-600'
          }`}
          onClick={() => setPrinterType('browser')}
        >
          <CardContent className="pt-6 text-center">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
              printerType === 'browser' ? 'bg-green-500/20' : 'bg-gray-700'
            }`}>
              <Globe className={`w-8 h-8 ${printerType === 'browser' ? 'text-green-400' : 'text-gray-400'}`} />
            </div>
            <h3 className="font-semibold text-white mb-2">Browser-Druck</h3>
            <p className="text-gray-500 text-sm">Drucken über Browser-Dialog</p>
            <Badge className="mt-3 bg-green-500/20 text-green-400 border-green-500/50">
              Sofort einsatzbereit
            </Badge>
          </CardContent>
        </Card>
        
        {/* Netzwerk-Drucker */}
        <Card
          data-testid="printer-type-network"
          className={`cursor-pointer transition-all ${
            printerType === 'network' 
              ? 'bg-blue-600/20 border-blue-500 ring-2 ring-blue-500' 
              : 'bg-gray-800 border-gray-700 hover:border-gray-600'
          }`}
          onClick={() => setPrinterType('network')}
        >
          <CardContent className="pt-6 text-center">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
              printerType === 'network' ? 'bg-blue-500/20' : 'bg-gray-700'
            }`}>
              <Wifi className={`w-8 h-8 ${printerType === 'network' ? 'text-blue-400' : 'text-gray-400'}`} />
            </div>
            <h3 className="font-semibold text-white mb-2">Netzwerk-Drucker</h3>
            <p className="text-gray-500 text-sm">Epson, Star, Bixolon über LAN</p>
            <Badge className="mt-3 bg-blue-500/20 text-blue-400 border-blue-500/50">
              Empfohlen für Gastronomie
            </Badge>
          </CardContent>
        </Card>
        
        {/* USB/Bluetooth-Drucker */}
        <Card
          data-testid="printer-type-usb"
          className={`cursor-pointer transition-all ${
            printerType === 'usb' 
              ? 'bg-purple-600/20 border-purple-500 ring-2 ring-purple-500' 
              : 'bg-gray-800 border-gray-700 hover:border-gray-600'
          }`}
          onClick={() => setPrinterType('usb')}
        >
          <CardContent className="pt-6 text-center">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
              printerType === 'usb' ? 'bg-purple-500/20' : 'bg-gray-700'
            }`}>
              <Printer className={`w-8 h-8 ${printerType === 'usb' ? 'text-purple-400' : 'text-gray-400'}`} />
            </div>
            <h3 className="font-semibold text-white mb-2">USB / Bluetooth</h3>
            <p className="text-gray-500 text-sm">Lokaler Drucker am PC</p>
            <Badge className="mt-3 bg-amber-500/20 text-amber-400 border-amber-500/50">
              Agent erforderlich
            </Badge>
          </CardContent>
        </Card>
      </div>
      
      {/* Netzwerk-Drucker Konfiguration */}
      {printerType === 'network' && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wifi className="w-5 h-5 text-blue-400" />
              Netzwerk-Drucker konfigurieren
            </CardTitle>
            <CardDescription>Geben Sie die IP-Adresse Ihres Bondruckers ein</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Drucker-Name</Label>
                <Input
                  value={printerName}
                  onChange={(e) => setPrinterName(e.target.value)}
                  placeholder="z.B. Kassendrucker"
                  className="bg-gray-900 border-gray-700"
                  data-testid="printer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">IP-Adresse *</Label>
                <Input
                  value={printerIp}
                  onChange={(e) => setPrinterIp(e.target.value)}
                  placeholder="z.B. 192.168.1.100"
                  className="bg-gray-900 border-gray-700"
                  data-testid="printer-ip-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Port</Label>
                <Input
                  type="number"
                  value={printerPort}
                  onChange={(e) => setPrinterPort(parseInt(e.target.value) || 9100)}
                  className="bg-gray-900 border-gray-700"
                  data-testid="printer-port-input"
                />
                <p className="text-gray-600 text-xs">Standard: 9100</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Papierbreite</Label>
                <Select value={String(paperWidth)} onValueChange={(v) => setPaperWidth(parseInt(v))}>
                  <SelectTrigger className="bg-gray-900 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="80">80mm (Standard)</SelectItem>
                    <SelectItem value="58">58mm (Kompakt)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Test-Button */}
            <div className="flex items-center gap-4 pt-4 border-t border-gray-700">
              <Button
                onClick={testPrinter}
                disabled={!printerIp || printerTesting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="test-printer-btn"
              >
                {printerTesting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Printer className="w-4 h-4 mr-2" />
                )}
                Testdruck senden
              </Button>
              
              {printerTestResult && (
                <div className={`flex items-center gap-2 ${printerTestResult.success ? 'text-green-400' : 'text-red-400'}`}>
                  {printerTestResult.success ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  <span className="text-sm">{printerTestResult.message}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* USB-Drucker Hinweis */}
      {printerType === 'usb' && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
              <div>
                <p className="text-amber-300 font-medium">Cash-X Print Agent erforderlich</p>
                <p className="text-gray-400 text-sm mt-1">
                  Für USB- und Bluetooth-Drucker benötigen Sie den Cash-X Print Agent auf Ihrem Computer.
                </p>
                <Button
                  variant="outline"
                  className="mt-4 border-amber-500/50 text-amber-300 hover:bg-amber-500/20"
                  onClick={() => window.open('/app/print-agent', '_blank')}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Agent herunterladen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Browser-Druck Info */}
      {printerType === 'browser' && (
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
              <div>
                <p className="text-green-300 font-medium">Keine weitere Einrichtung nötig</p>
                <p className="text-gray-400 text-sm mt-1">
                  Bons werden über den Browser-Druckdialog ausgegeben. Ideal zum Einstieg – 
                  Sie können jederzeit auf einen Bondrucker wechseln.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Empfohlene Drucker */}
      {printerRecommendation?.suggested_printers?.length > 0 && printerType === 'network' && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" />
            Empfohlene Bondrucker
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {printerRecommendation.suggested_printers.map((printer, idx) => (
              <Card key={idx} className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-white">{printer.brand}</p>
                      <p className="text-blue-400">{printer.model}</p>
                    </div>
                    {printer.popular && (
                      <Badge className="bg-amber-500/20 text-amber-400">Beliebt</Badge>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm mb-2">{printer.recommendation}</p>
                  <p className="text-green-400 font-medium">{printer.price_range}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {printer.connection?.map((conn, i) => (
                      <Badge key={i} variant="outline" className="text-xs text-gray-400 border-gray-600">
                        {conn.toUpperCase()}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep7_Finish = () => (
    <div className="text-center py-12">
      <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-12 h-12 text-green-400" />
      </div>
      
      <h2 className="text-3xl font-bold text-white mb-4">Alles fertig! 🎉</h2>
      <p className="text-gray-400 text-lg mb-8">Ihr Kassensystem ist einsatzbereit</p>
      
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <Store className="w-8 h-8 mx-auto text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-white">{businessName}</p>
            <p className="text-gray-500 text-sm">{BUSINESS_TYPES.find(t => t.id === businessType)?.label}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <Users className="w-8 h-8 mx-auto text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-white">{tableCount}</p>
            <p className="text-gray-500 text-sm">Tische</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <ChefHat className="w-8 h-8 mx-auto text-orange-400 mb-2" />
            <p className="text-2xl font-bold text-white">
              {categories.reduce((sum, c) => sum + (c.products?.filter(p => p.approved !== false).length || 0), 0)}
            </p>
            <p className="text-gray-500 text-sm">Produkte</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <Printer className="w-8 h-8 mx-auto text-green-400 mb-2" />
            <p className="text-2xl font-bold text-white">✓</p>
            <p className="text-gray-500 text-sm">
              {printerType === 'browser' ? 'Browser-Druck' : printerType === 'network' ? 'Netzwerk' : 'USB/BT'}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Button
        size="lg"
        className="bg-green-600 hover:bg-green-700 text-lg px-8 py-6"
        onClick={() => navigate('/app')}
        data-testid="go-to-dashboard-btn"
      >
        <Crown className="w-5 h-5 mr-2" />
        Zum Dashboard
      </Button>
      
      <p className="text-gray-600 text-sm mt-6">
        Drucker und weitere Einstellungen finden Sie unter Einstellungen → Drucker
      </p>
    </div>
  );

  // ==========================================================================
  // Main Render
  // ==========================================================================

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <span className="text-xl font-bold">C</span>
              </div>
              <div>
                <h1 className="text-xl font-bold">Cash-X Einrichtung</h1>
                <p className="text-gray-500 text-sm">{businessName || 'Ihr Restaurant'} • Enterprise</p>
              </div>
            </div>
            <Badge variant="outline" className="text-blue-400 border-blue-400">
              Schritt {currentStep + 1} von {STEPS.length}
            </Badge>
          </div>
          
          {/* Progress */}
          <Progress value={progress} className="h-2" />
          
          {/* Step Indicators */}
          <div className="flex justify-between mt-4">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              return (
                <div
                  key={step.id}
                  className={`flex flex-col items-center gap-1 ${
                    isActive ? 'text-blue-400' : isCompleted ? 'text-green-400' : 'text-gray-600'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isActive ? 'bg-blue-600' : isCompleted ? 'bg-green-600' : 'bg-gray-800'
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className="text-xs hidden md:block">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {currentStep === 0 && renderStep1_Business()}
        {currentStep === 1 && renderStep2_Services()}
        {currentStep === 2 && renderStep3_Location()}
        {currentStep === 3 && renderStep4_Menu()}
        {currentStep === 4 && renderStep5_Review()}
        {currentStep === 5 && renderStep6_Printer()}
        {currentStep === 6 && renderStep7_Finish()}
      </div>
      
      {/* Footer Navigation */}
      {currentStep < 6 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="border-gray-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            
            {currentStep === 5 ? (
              <Button
                onClick={createTablesAndFinish}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="complete-setup-btn"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Einrichtung abschließen
              </Button>
            ) : (
              <Button
                onClick={nextStep}
                disabled={!canProceed()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="next-step-btn"
              >
                Weiter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
